import type { WPTechnician } from "./technician.interface";
import { INTERNAL_PROJECT_IDS } from "./internal-project.const";
import type {
  BarData,
  BarDataValue,
} from "../welcome/capacity-bars/capacity-bars.model";
import type { WPOperation } from "./operation.interface";
import { PAUtil } from "../_components/planning-assistant/classes/util";
import type { RouteNode } from "../welcome/route-visualization/route-visualization.model";
import type { AnimatableTour } from "../_components/planning-assistant/classes/tour-animation";
import type { MapRoute } from "./planning-assistant.interface";
import type { DistanceCalculator } from "../_components/planning-assistant/classes/location";
import { DistanceCalculableLocation } from "../_components/planning-assistant/classes/location";
import type {
  OperationWithStore,
  TourIdxShowTour
} from "../_components/planning-assistant/reuseable-components/tour-stopover/tour-stopover.model";
import { PATimeControl } from "../_components/planning-assistant/singletons/pa-time-control";
import { PASettingsControl } from "../_components/planning-assistant/singletons/pa-settings-control";

export interface TechnicianTourRoute {
  hours: {
    total_hours: number
    driving_hours: number
    working_hours: number
    unused_capacity_hours: number
    additional_free_hours: number
    home_driving_hours: number
  },
  route: Route
  absence?: string
  errors?: string[]
}

export type Route = (OperationWaypoint | DrivingConnection)[]

export interface Coordinates {
  latitude: number,
  longitude: number
}

export interface CheckItem {
  status: 'done' | 'active' | 'open',
}

export interface OperationWaypoint extends CheckItem {
  type: 'OperationWaypoint',
  work_time: number,
  start_time: number,
  operation: WPOperation
}

export interface DrivingConnection extends CheckItem {
  type: 'DrivingConnection',
  from_location: DistanceCalculableLocation,
  to_location: DistanceCalculableLocation,
  destination: string
  estimated_minutes: number,
  driven_minutes?: number
  driving_start_time: number,
}

// Kein Standortwechsel in Routen für Aufträge aus internen Bentomax-Projekten —
// IDs zentral in internal-project.const.ts (hawk#556, Aufgabe D).
export const NO_LOCATION_CHANGE_FOR_PROJECTS: number[] = INTERNAL_PROJECT_IDS
export const NO_LOCATION_CHANGE_FOR_DESTINATION_COORDINATES: { lat: number, lng: number }[] = [
  { lat: 54.4119, lng: 10.1303 }, // no location change in routes for operations with Bentomax central coordinates
  { lat: 54.4128, lng: 10.1307 },
  { lat: 54.4116, lng: 10.1294 }
]
const MAX_TAGES_AUSLASTUNG = 8.75 // 8h Arbeit + 0:45h Pause
export class TechnicianTour implements AnimatableTour, TourIdxShowTour {

  private _capacityBarData?: BarData;
  private _routeNodes?: RouteNode[];
  private _tourRoute?: TechnicianTourRoute
  private _operations?: OperationWithStore[]

  private _color: string = 'white';
  private readonly _useDrivingTimeFactor: boolean = false;

  constructor(
    private technicianTravelData: WPTechnician & { tour?: TechnicianTour, day_absence?: string },
    private distanceCalculator: DistanceCalculator,
    private storeCollection: StoreCollection,
    private local_day_ts: number,
    private config?: {use_driving_time_factor?: boolean}
  ) {
    this.generateRoute().then(
      tour_route => {
        this._capacityBarData = this.generateCapacityBarData(tour_route)
        this._routeNodes = this.generateRouteVisualizationData(tour_route.route)
        this._tourRoute = tour_route
        this._operations = this.generateTourStoreOperations(tour_route)
      }
    )
    if (config?.use_driving_time_factor) this._useDrivingTimeFactor = true
  }

  private generateTourStoreOperations(tour_route: TechnicianTourRoute) {
    return tour_route.route.filter(
      item => item.type == 'OperationWaypoint'
    ).map(
      waypoint => {
        return {
          storeId: this.storeCollection.getStoreIdForCoordinates(waypoint.operation),
          id: waypoint.operation.id
        }
      }
    );
  }

  get operations(): OperationWithStore[] {
    return this._operations;
  }

  get tourRoute(): TechnicianTourRoute {
    return this._tourRoute
  }

  getColor(): string {
    return this._color
  }

  get color() {
    return this._color
  }

  setColor(color: string): void {
    this._color = color
  }

  toMapRoutes(): MapRoute[] {
    return this._tourRoute.route.filter(
      item => item.type == 'DrivingConnection'
    ).map(driving_connection => {
      return {
        from: driving_connection.from_location,
        to: driving_connection.to_location,
        color: this.getTourAnimationColor(),
        dashed: false,
        line_width: 5,
        popup_text: null,
      }
    })
  }

  getTourAnimationColor(): string {
    const tour = this.technicianTravelData.tour
    return tour ? tour._color : 'white'
  }

  get capacityBarData(): BarData {
    return this._capacityBarData;
  }

  get routeNodes(): RouteNode[] {
    return this._routeNodes;
  }

  private async generateRoute(): Promise<TechnicianTourRoute> {
    if (this.technicianTravelData.day_absence) {
      return this.generateAbsenceRoute()
    } else {

      const { route, driving_hours, working_hours, home_driving_hours } = await this.generateTechniciansOperationsRoute(this.technicianTravelData.day_operations)
      let total_hours = driving_hours + working_hours

      if (total_hours > 0) {
        total_hours += 0.75 // add 45min break
      }

      // Lücke vor Tour-Start berechnen (Startzeit bis erste Losfahrzeit)
      const tour_route_for_gap: TechnicianTourRoute = { route, hours: null, errors: [] }
      const first_departure_hours = this.getFirstDepartureHours(tour_route_for_gap)
      const technician_start_hours = this.getTechnicianStartHours()
      let gap_before_tour_hours = 0
      if (first_departure_hours !== undefined && first_departure_hours > technician_start_hours) {
        gap_before_tour_hours = first_departure_hours - technician_start_hours
      }


      // Für leere Touren heute: bereits vergangene Zeit seit Startzeit als Gap berücksichtigen
      const todays_local_day_ts = PATimeControl.Instance.dateToTimestamp(new Date(), true, false)
      if (total_hours === 0 && todays_local_day_ts === this.local_day_ts) {
        const hours_now = this.getHoursNow()
        if (hours_now > technician_start_hours) {
          gap_before_tour_hours = hours_now - technician_start_hours
        }
      }

      const effective_total = total_hours + gap_before_tour_hours

      return {
        errors: [],
        hours: {
          total_hours,
          working_hours,
          driving_hours,
          unused_capacity_hours: Math.max(MAX_TAGES_AUSLASTUNG - effective_total, 0),
          // 8h Arbeit + 0:45h Pause + 2h zusätzliche freie Zeit
          additional_free_hours: Math.min(2, Math.max((MAX_TAGES_AUSLASTUNG + 2) - effective_total, 0)),
          home_driving_hours
        },
        route: route
      }
    }
  }

  async waitForBarData(): Promise<BarData> {
    await PAUtil.sleepUntilConditionBreaks(
      () => {
        return !this._capacityBarData
      }
    )
    return this._capacityBarData
  }

  async waitForRouteNodes(): Promise<RouteNode[]> {
    await PAUtil.sleepUntilConditionBreaks(
      () => {
        return !this._routeNodes
      }
    )
    return this._routeNodes
  }

  generateAbsenceRoute(): TechnicianTourRoute {
    return {
      absence: this.technicianTravelData.day_absence,
      hours: {
        total_hours: 8,
        working_hours: 8,
        additional_free_hours: 0,
        driving_hours: 0,
        unused_capacity_hours: 0,
        home_driving_hours: 0
      },
      route: []
    }
  }

  async generateTechniciansOperationsRoute(operations: WPOperation[]): Promise<{
    route: Route,
    driving_hours: number,
    working_hours: number,
    home_driving_hours: number
  }> {
    const technician = this.technicianTravelData
    let last_location = new DistanceCalculableLocation(
      { latitude: technician.day_start_lat, longitude: technician.day_start_lon },
      this.distanceCalculator
    )
    let home_driving_start_time = PASettingsControl.Instance.getWorkStartMinutesForTechnician(this.technicianTravelData.id) // individuelle Startzeit in Tages-Minuten
    const route: Route = []

    let driving_hours = 0
    let working_hours = 0

    for (const operation of operations) {
      const driving_connection_to_operation = await this.getOperationDrivingConnection(operation, last_location)
      if (driving_connection_to_operation) {
        route.push(driving_connection_to_operation)
        const use_minutes = driving_connection_to_operation.driven_minutes || driving_connection_to_operation.estimated_minutes
        driving_hours += use_minutes / 60
        last_location = driving_connection_to_operation.to_location
      }


      const work_time = this.operationWorkTime(operation)
      working_hours += (work_time / 60)
      const operation_start_timestamp = this.operationsStartTimestamp(operation)

      route.push({
        type: 'OperationWaypoint',
        operation: operation,
        status: operation.finished ? 'done' : (operation.on_site ? 'active' : 'open'),
        start_time: this.timestampToDayMinutes(operation_start_timestamp),
        work_time: work_time
      })

      if (operations.indexOf(operation) == operations.length - 1) {
        const finished_operation_timestamp = operation_start_timestamp + work_time * 60 * 1000;
        home_driving_start_time = this.timestampToDayMinutes(finished_operation_timestamp)
      }
    }

    let home_driving_hours = 0
    const home_driving_connection = await this.getHomeDrivingConnection(last_location, home_driving_start_time)
    if (home_driving_connection) {
      route.push(home_driving_connection)
      const use_minutes = home_driving_connection.driven_minutes || home_driving_connection.estimated_minutes
      home_driving_hours = use_minutes / 60
      driving_hours += home_driving_hours
    }

    return { route, driving_hours, working_hours, home_driving_hours }
  }

  async getOperationDrivingConnection(operation: WPOperation, from_location: DistanceCalculableLocation): Promise<DrivingConnection | undefined> {
    const { driven_minutes, estimated_minutes } = await this.drivingTimeToOperation(operation, from_location)

    if (driven_minutes > 0 || estimated_minutes > 0) {

      let start_timestamp: number
      if (operation.travel_start) {
        start_timestamp = new Date(operation.travel_start).getTime()
      } else {
        const use_minutes = driven_minutes || estimated_minutes
        if (operation.on_site) {
          start_timestamp = new Date(operation.on_site).getTime() - use_minutes * 60 * 1000
        } else {
          start_timestamp = new Date(operation.operation_date).getTime() - use_minutes * 60 * 1000
        }
      }

      return {
        from_location: from_location,
        to_location: new DistanceCalculableLocation(
          { latitude: operation.lat, longitude: operation.lon },
          this.distanceCalculator
        ),
        estimated_minutes: estimated_minutes,
        driven_minutes: driven_minutes,
        driving_start_time: this.timestampToDayMinutes(start_timestamp),
        type: "DrivingConnection",
        status: operation.on_site ? 'done' : ( operation.travel_start ? 'active' : 'open'),
        destination: `Fahrt nach ${operation.city}`
      }
    }
    return
  }

  async getHomeDrivingConnection(from_location: DistanceCalculableLocation, driving_start_time: number): Promise<DrivingConnection | undefined> {
    const driving_time = await this.getHomeDrivingMinutes(from_location)

    if (driving_time > 0) {
      return {
        from_location: from_location,
        to_location: new DistanceCalculableLocation(
          { latitude: this.technicianTravelData.day_end_lat, longitude: this.technicianTravelData.day_end_lon },
          this.distanceCalculator
        ),
        estimated_minutes: driving_time,
        driven_minutes: null,
        driving_start_time: driving_start_time,
        type: "DrivingConnection",
        destination: 'Heimfahrt',
        status: 'open'
      }
    }
    return
  }

  private async drivingTimeToOperation(operation: WPOperation, from_location: DistanceCalculableLocation): Promise<{ estimated_minutes: number, driven_minutes: number }>{
    let estimated_minutes = 0
    let driven_minutes = 0
    if (
      !NO_LOCATION_CHANGE_FOR_PROJECTS.includes(operation.project_id) &&
      !NO_LOCATION_CHANGE_FOR_DESTINATION_COORDINATES.find(coords => coords.lat == operation.lat && coords.lng == operation.lon)
    ) {
      estimated_minutes = await this.mapboxDrivingTimeToOperation(operation, from_location)
      if (operation.travel_start && operation.on_site) {
        driven_minutes = (new Date(operation.on_site).getTime() - new Date(operation.travel_start).getTime()) / (1000 * 60)
      }
    }

    return { estimated_minutes, driven_minutes }
  }

  private async mapboxDrivingTimeToOperation(operation: WPOperation, from_location: DistanceCalculableLocation): Promise<number> {
    const coordinates = from_location.coordinates
    if (operation.lat == coordinates.latitude && operation.lon == coordinates.longitude) {
      return 0
    } else {
      const driving_time_factor = this._useDrivingTimeFactor ? this.technicianTravelData.driving_time_factor : 1
      return (await from_location.getStreetDistanceToCoordinates(operation.lat, operation.lon)).duration * driving_time_factor
    }
  }

  operationsStartTimestamp(operation: WPOperation): number {
    let use_date: Date
    if (operation.on_site) {
      use_date = new Date(operation.on_site)
    } else {
      use_date = new Date(operation.operation_date)
    }

    return use_date.getTime()
  }

  timestampToDayMinutes(timestamp: number): number {
    const date = new Date(timestamp)
    date.setHours(0)
    date.setMinutes(0)
    date.setMilliseconds(0)
    return (timestamp - date.getTime()) / (60 * 1000)
  }

  // get work time in minutes
  operationWorkTime(operation: WPOperation): number {
    if (operation.on_site && operation.finished) {
      return (new Date(operation.finished).getTime() - new Date(operation.on_site).getTime()) / (1000 * 60)
    } else {
      return operation.time_estimation
    }
  }

  async getHomeDrivingMinutes(from_location: DistanceCalculableLocation): Promise<number> {
    const technician = this.technicianTravelData
    let home_driving_minutes = 0
    if (!technician.travel_technician_date_id) {
      const driving_time_factor = this._useDrivingTimeFactor ? technician.driving_time_factor : 1
      const driving_home_duration = (await from_location.getStreetDistanceToCoordinates(technician.day_end_lat, technician.day_end_lon)).duration * driving_time_factor
      if (driving_home_duration) {
        home_driving_minutes += driving_home_duration
      }
    }
    return home_driving_minutes
  }

  private hoursToTimeString(hours: number): string {
    let full_hour = Math.floor(hours)
    let minutes = Math.round((hours - full_hour) * 60)

    if (minutes == 60) {
      full_hour += 1
      minutes = 0
    }

    return `${full_hour}:${minutes < 10 ? '0' : ''}${minutes}`
  }

  /*private async distanceBetweenCoordinates(lat1: number, lon1: number, lat2: number, lon2: number): Promise<{ distance: number, duration: number, valid: boolean }> {
    if (lat1 == lat2 && lon1 == lon2) return { distance: 0, duration: 0, valid: true }

    // check local storage first
    const storage_key = `route:${lat1},${lon1};${lat2},${lon2}`
    let local_storage_value = localStorage.getItem(storage_key)
    let stored_route = (local_storage_value ? JSON.parse(local_storage_value) as MBRoute : null)

    if (stored_route) {
      return {distance: stored_route.distance, duration: stored_route.duration, valid: isFinite(stored_route.duration)}
    } else {
      return await new Promise(resolve => {
        this.operationService.getDistanceBetweenCoordinates(lat1, lon1, lat2, lon2).subscribe(
          data => {
            const route: MBRoute = {distance: data.distance, duration: data.duration}
            const route_string = JSON.stringify(route)
            PAUtil.setLocalStorageItem(storage_key, route_string)
            resolve(data)
          },
          error => {
            console.log(error)
            resolve({ distance: -1, duration: -1, valid: false })
          }
        )
      })
    }
  }*/

  private generateCapacityBarData(route: TechnicianTourRoute): BarData {
    const technician = this.technicianTravelData
    const reversed_technician_name = technician.lastname + ', ' + technician.firstname
    return {
      description: reversed_technician_name.length > 21 ? `${reversed_technician_name.slice(0, 18)}...` : reversed_technician_name,
      values: this.generateCapacityBarDataValues(route),
      start_at_unit_amount: this.getBarDataStartUnitAmount(route),
      style: this.getBarDataStyle(route),
      filter_attributes: this.getBarDataFilterAttributes(),
      options: {
        'Reisetechniker': !!technician.travel_technician_date_id
      },
      utilizationPercent: this.getUtilizationPercent(route),
      // Abwesenheitsgrund (aus Absence.io) für den Badge in der Tagesansicht
      absence_reason: route.absence
    }
  }

  // Auslastung relativ zur regulären Tageskapazität (8h Arbeit + 0:45h Pause, siehe unused_capacity_hours-Berechnung)
  private getUtilizationPercent(route: TechnicianTourRoute): number | undefined {
    if (route.absence) return undefined
    return Math.round((route.hours.total_hours / MAX_TAGES_AUSLASTUNG) * 100)
  }

  // Individuelle Startzeit des Technikers in Stunden (z.B. 9.5 für 9:30)
  private getTechnicianStartHours(): number {
    return PASettingsControl.Instance.getWorkStartMinutesForTechnician(this.technicianTravelData.id) / 60
  }

  // Berechnet die Losfahrzeit zum ersten Kunden (erste Operation Start - Fahrzeit dorthin)
  private getFirstDepartureHours(tour_route?: TechnicianTourRoute): number | undefined {
    if (!tour_route?.route?.length) return undefined
    const first_driving = tour_route.route.find(item => item.type === 'DrivingConnection') as DrivingConnection | undefined
    const first_operation = tour_route.route.find(item => item.type === 'OperationWaypoint') as OperationWaypoint | undefined
    if (first_driving) {
      return first_driving.driving_start_time / 60
    } else if (first_operation) {
      return first_operation.start_time / 60
    }
    return undefined
  }

  private getBarDataStartUnitAmount(tour_route?: TechnicianTourRoute): number {
    const technician = this.technicianTravelData
    const technician_start_hours = this.getTechnicianStartHours()

    let result: number
    if (technician.day_operations.length && !technician.day_absence) {
      const day_travel_start = technician.day_operations[0].travel_start
      if (day_travel_start) {
        // Live-Daten: tatsächliche Losfahrzeit als Ankerpunkt (oder Techniker-Startzeit falls früher)
        result = Math.min(this.getHoursOnDate(new Date(day_travel_start)), technician_start_hours)
      } else {
        // Geplante Tour ohne Live-Daten: frühester Wert aus geplanter Losfahrzeit und Techniker-Startzeit
        const first_departure = this.getFirstDepartureHours(tour_route)
        result = first_departure !== undefined ? Math.min(first_departure, technician_start_hours) : technician_start_hours
      }
    } else {
      // Keine Aufträge oder Absence -> individuelle Startzeit
      result = technician_start_hours
    }
    // Nicht unter 0:00 Uhr (Mitternacht) gehen
    return Math.max(0, result)
  }

  private generateCapacityBarDataValues(route: TechnicianTourRoute): BarDataValue[] {
    const res: BarDataValue[] = []
    const total_hours = route.hours.total_hours
    const absence = route.absence
    const technician_start_hours = this.getTechnicianStartHours()
    const todays_local_day_ts = PATimeControl.Instance.dateToTimestamp(new Date(), true, false)
    const is_today = todays_local_day_ts == this.local_day_ts

    if (absence) {
      return [{
        tooltip: {
          infos: [absence],
          errors: []
        },
        icon: null,
        unit_amount: 8,
        color: 'lightgray'
      }]
    } else {
      if (total_hours > 0) {
        // Roten Block vor Tour-Start einfügen wenn Lücke zwischen Startzeit und erster Losfahrzeit
        const first_departure_hours = this.getFirstDepartureHours(route)
        if (first_departure_hours !== undefined && first_departure_hours > technician_start_hours) {
          const gap_hours = first_departure_hours - technician_start_hours
          if (gap_hours > 0.05) { // Nur anzeigen wenn > 3 Minuten
            res.push({
              color: 'rgb(253,0,0)',
              unit_amount: gap_hours,
              tooltip: {
                infos: [`Nicht genutzte Zeit vor Tour-Start (${this.hoursToTimeString(gap_hours)}h)`],
                errors: []
              },
            })
          }
        }

        const tooltip = {
          infos: [
            `Geplante Tour: ${this.hoursToTimeString(total_hours)}h`,
            `Davon Fahrtzeit: ${this.hoursToTimeString(route.hours.driving_hours)}h`,
            `Davon Auftragszeit: ${this.hoursToTimeString(route.hours.working_hours)}h`,
            `Davon Pause: 0:45h`,
          ],
          errors: route.errors || []
        }

        let icon: 'loading_error' | 'finished' | null = null
        if (this.technicianTravelData.day_operations.length) {
          const last_operation = this.technicianTravelData.day_operations[this.technicianTravelData.day_operations.length - 1]
          if (last_operation.finished) icon = 'finished'
        }

        res.push({
          color: 'rgb(0,128,0)',
          icon: icon,
          tooltip: tooltip,
          unit_amount: icon == 'finished' ? (total_hours - route.hours.home_driving_hours) : total_hours
        })

        if (icon == 'finished') {
          res.push({
            color: 'rgb(0,128,0)',
            icon: "home_travel",
            tooltip: {
              infos: [`Geschätzte Heimfahrt: ${this.hoursToTimeString(route.hours.home_driving_hours)}h`],
              errors: []
            },
            unit_amount: route.hours.home_driving_hours
          })
        }
      } else if (is_today && this.getHoursNow() > technician_start_hours && !this.technicianTravelData.day_operations.length) {
        // Heute, keine Tour, Startzeit überschritten: Roter Block bis jetzt
        const wasted_hours = this.getHoursNow() - technician_start_hours
        res.push({
          color: 'rgb(253,0,0)',
          unit_amount: wasted_hours,
          tooltip: {
            infos: [`Nicht genutzte Zeit seit Arbeitsbeginn (${this.hoursToTimeString(wasted_hours)}h)`],
            errors: []
          },
        })
      }

      // Bei leeren Touren heute mit überschrittener Startzeit: Restkapazität nur als Umrandung anzeigen
      const show_remaining_as_bordered = is_today && !this.technicianTravelData.day_operations.length && this.getHoursNow() > technician_start_hours

      const unused_hours = route.hours.unused_capacity_hours
      if (unused_hours) {
        res.push({
          color: 'rgb(253,0,0)',
          unit_amount: unused_hours,
          style: show_remaining_as_bordered ? 'bordered' : undefined,
          tooltip: {
            infos: [show_remaining_as_bordered
              ? `Noch nutzbare Kapazität (${this.hoursToTimeString(unused_hours)}h)`
              : `Der Techniker hat nicht genutzte Kapazitäten! (${this.hoursToTimeString(unused_hours)}h)`],
            errors: []
          },
        })
      }

      const free_hours = route.hours.additional_free_hours
      if (free_hours) {
        res.push({
          color: 'rgb(255,132,132)',
          unit_amount: free_hours,
          style: show_remaining_as_bordered ? 'bordered' : undefined,
          tooltip: {
            infos: [show_remaining_as_bordered
              ? `Weitere nutzbare Zeit (${this.hoursToTimeString(free_hours)}h)`
              : `Der Techniker hat noch weitere freie Zeit (${this.hoursToTimeString(free_hours)}h)`],
            errors: []
          },
        })
      }

      return res
    }
  }

  private generateRouteVisualizationData(route: Route): RouteNode[] {

    if (this.technicianTravelData.day_absence) {
      return [{
        duration: 480,
        icon: 'absence',
        main_description: '',
        day_start_time: null,
        estimated_duration: 480,
        status: 'done',
        header: `Abwesenheit: ${this.technicianTravelData.day_absence}`
      }]
    }

    const route_nodes: RouteNode[] = []
    const route_operations = route.filter(r => r.type == 'OperationWaypoint')
    for (const route_item of route) {
      if (route_item.type == 'OperationWaypoint') {
        const op = route_item.operation
        route_nodes.push({
          header: `Auftrag ${op.company || ''}`,
          main_description: op.description,
          icon: 'working',
          duration: route_item.work_time,
          estimated_duration: op.time_estimation,
          day_start_time: route_item.start_time,
          link: `/a/ticket/${op.ticket_id}?operation_id=${op.id}`,
          status: route_item.status,
          index: route_operations.indexOf(route_item)
        })
      }
      if (route_item.type == 'DrivingConnection') {
        route_nodes.push({
          header: route_item.destination,
          status: route_item.status,
          main_description: '',
          icon: 'driving',
          duration: route_item.driven_minutes,
          estimated_duration: route_item.estimated_minutes,
          day_start_time: route_item.driving_start_time
        })
      }
    }
    return route_nodes
  }

  private getBarDataStyle(route?: TechnicianTourRoute): 'filled' | 'bordered' {
    if (this.technicianTravelData.day_operations.length || this.technicianTravelData.day_absence) return 'filled'
    // Heute: Wenn Startzeit überschritten und keine Tour, filled anzeigen (roter Block bis jetzt)
    const todays_local_day_ts = PATimeControl.Instance.dateToTimestamp(new Date(), true, false)
    if (todays_local_day_ts == this.local_day_ts && this.getHoursNow() > this.getTechnicianStartHours()) return 'filled'
    return 'bordered'
  }

  private getBarDataFilterAttributes(): string[] {
    const res: string[] = []
    const technician = this.technicianTravelData
    if (!technician.day_operations.length) {
      if (!technician.day_absence) res.push('Ohne Auftrag')
    } else {
      if (!technician.day_operations.find(op => !op.finished)) {
        res.push('Alle Aufträge abgeschlossen')
      } else {
        const started_operations = technician.day_operations.filter(op => op.travel_start)
        if (started_operations.length) {
          const last_started_operation_index = technician.day_operations.indexOf(started_operations[started_operations.length - 1])
          if (technician.day_operations.length - 1 == last_started_operation_index) {
            res.push('Im letzten Auftrag')
          }
        }
      }

    }

    return res;
  }

  private getHoursNow(): number {
    const date_now = new Date()
    return this.getHoursOnDate(date_now)
  }

  private getHoursOnDate(date: Date): number {
    const hour_now = date.getHours()
    const minute_now = date.getMinutes()
    return hour_now + (minute_now / 60)
  }

}

interface Store {id: number, lat: number, lon: number}

export class StoreCollection {

  readonly stores: Store[] = []

  constructor() {
  }

  getStoreIdForCoordinates(coordinates: {lat: number, lon: number}): number {
    return this.getStoreForCoordinates(coordinates).id
  }

  getStoreForCoordinates(coordinates: {lat: number, lon: number}): Store {
    let store = this.stores.find(s => s.lat == coordinates.lat && s.lon == coordinates.lon)
    if (!store) {
      const new_store = {
        id: this.stores.length + 1,
        lat: coordinates.lat,
        lon: coordinates.lon
      }
      this.stores.push(new_store)
      store = new_store
    }
    return store
  }

  getStore(sid: number): Store {
    return this.stores.find(s => s.id == sid)
  }
}
